import type { Rating } from "../types/rating";
import type { AchievementExperienceItem, ProgressExperienceItem } from "../types/experience";
import { isUnknownArray, isRecord } from "../types/data";
// New XP progression system with 100 levels
// Based on the provided XP table
import { getChallengeExperience } from "./challenges";
import {
  filterExperienceRatings,
  getMediaType
} from "./gameProgressEligibility";
import { getAchievementExperience } from "./achievements";
import { getWeeklyStreakLedgerExperience } from "./weeklyStreakEvents";

// Generate XP progression table: level -> cumulative XP required
const generateXPProgression = () => {
  const xpTable: Record<number, number> = {};
  let cumulativeXP = 0;
  
  // Levels 1-80: 10 XP increase per level
  for (let level = 1; level <= 80; level++) {
    const xpForLevel = level * 10;
    cumulativeXP += xpForLevel;
    xpTable[level] = cumulativeXP;
  }
  
  // Levels 81-90: 30 XP increase per level
  for (let level = 81; level <= 90; level++) {
    const xpForLevel = 800 + (level - 80) * 30;
    cumulativeXP += xpForLevel;
    xpTable[level] = cumulativeXP;
  }
  
  // Levels 91-99: 50 XP increase per level, plus additional 50 XP every 2 levels
  let additionalXP = 0;
  for (let level = 91; level <= 99; level++) {
    const xpForLevel = 1100 + (level - 90) * 50 + additionalXP;
    cumulativeXP += xpForLevel;
    xpTable[level] = cumulativeXP;
    if (level % 2 === 1) additionalXP += 50; // Add 50 XP every odd level
  }
  
  // Level 100
  xpTable[100] = 55800;
  
  return xpTable;
};

export const XP_PROGRESSION = generateXPProgression();

const toNonNegativeNumber = (value: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, number) : 0;
};

const normalizeLevel = (value: unknown) => Math.max(1, Math.floor(Number(value) || 1));
const addSafeExperience = (total: number, value: number) =>
  Math.min(
    Number.MAX_SAFE_INTEGER,
    toNonNegativeNumber(total) + toNonNegativeNumber(value)
  );

const getRatingExperienceKey = (rating: Rating = {}) => {
  const filmId = rating?.filmId ?? rating?.id;
  if (filmId == null || String(filmId).trim() === "") return "";
  return `${getMediaType(rating)}:${String(filmId).trim()}`;
};

const getAchievementExperienceKey = (achievement: AchievementExperienceItem = {}) => {
  const id = achievement?.id;
  if (id != null && String(id).trim()) return `id:${String(id).trim()}`;
  const title = String(achievement?.title || "").trim().toLowerCase();
  return title ? `title:${title}` : "";
};

const getProgressExperienceKey = (item: ProgressExperienceItem = {}) => {
  const id = item?.id ?? item?.sourceId;
  if (id == null || String(id).trim() === "") return "";
  const sourceType = String(item?.sourceType || "challenge").trim();
  const instanceKey = sourceType === "challenge"
    ? String(Number(item?.startedAt) || "legacy")
    : "";
  return [sourceType, String(id).trim(), instanceKey].filter(Boolean).join(":");
};

const sumUniqueExperience = <T>(items: T[], getKey: (item: T) => string, getExperience: (item: T) => number) => {
  const keyedExperience = new Map<string, number>();
  let anonymousExperience = 0;

  (isUnknownArray(items) ? items : []).forEach((item) => {
    const experience = toNonNegativeNumber(getExperience(item));
    if (experience <= 0) return;
    const key = getKey(item);
    if (!key) {
      anonymousExperience = addSafeExperience(anonymousExperience, experience);
      return;
    }
    keyedExperience.set(
      key,
      Math.max(keyedExperience.get(key) || 0, experience)
    );
  });

  return [...keyedExperience.values()].reduce(
    (total, experience) => addSafeExperience(total, experience),
    anonymousExperience
  );
};

// Get cumulative XP for a specific level
export const getCumulativeXPForLevel = (level: string|number) => {
  const normalizedLevel = normalizeLevel(level);
  if (normalizedLevel <= 100) {
    return XP_PROGRESSION[normalizedLevel] || XP_PROGRESSION[100];
  }
  
  // For levels beyond 100, continue with the last pattern
  const baseLevel = 100;
  const baseXP = XP_PROGRESSION[100];
  const additionalLevels = normalizedLevel - baseLevel;
  
  // Continue with the same pattern: +1600 XP increment for each additional level
  return baseXP + (additionalLevels * 1600);
};

// Get XP required for a specific level (difference from previous level)
export const getExpForLevel = (level: number) => {
  const normalizedLevel = normalizeLevel(level);
  if (normalizedLevel <= 100) {
    return XP_PROGRESSION[normalizedLevel] -
      (XP_PROGRESSION[normalizedLevel - 1] || 0);
  }
  
  // For levels beyond 100, use the last pattern
  return 1600;
};

// Calculate level based on total XP (capped at 100)
export const calculateLevel = (totalExp: number) => {
  const normalizedExp = toNonNegativeNumber(totalExp);
  
  // If XP is greater than or equal to level 100 XP, return 100
  if (normalizedExp >= XP_PROGRESSION[100]) {
    return 100;
  }
  
  // For levels 1-99: use binary search in the table
  if (normalizedExp < XP_PROGRESSION[1]) return 1;
  
  let low = 1;
  let high = 99; // Only go up to 99 since we already checked for 100
  let level = 1;
  
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const cumulativeXP = XP_PROGRESSION[mid];
    
    if (cumulativeXP <= normalizedExp) {
      // Check if we should return this level or the next one
      if (mid === 99 || normalizedExp < XP_PROGRESSION[mid + 1]) {
        return mid;
      }
      level = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  
  return Math.min(level, 100); // Ensure we never return above 100
};

// Get level progress information with phase system
export const getLevelProgress = (totalExp: number) => {
  const normalizedExp = toNonNegativeNumber(totalExp);
  
  const level = calculateLevel(normalizedExp);
  
  let currentLevelXP: number, nextLevelXP: number, expForCurrentLevel: number, expNeededForNextLevel: number;
  let displayTotalExp = normalizedExp;
  
  // Handle levels 1-100 using the table
  if (level <= 100) {
    currentLevelXP = level > 1 ? getCumulativeXPForLevel(level) : 0;
    nextLevelXP = getCumulativeXPForLevel(level + 1);
    expForCurrentLevel = normalizedExp - currentLevelXP;
    expNeededForNextLevel = nextLevelXP - currentLevelXP;
    
    // For phase 2 (levels 81+), show phase XP only
    if (level > 80) {
      const phaseStartXP = XP_PROGRESSION[80];
      displayTotalExp = normalizedExp - phaseStartXP;
      expForCurrentLevel = displayTotalExp - (getCumulativeXPForLevel(level) - phaseStartXP);
    }
  }
  // Handle levels 101+ (continue with 2560 XP per level pattern)
  else {
    const baseLevel = 100;
    const baseXP = XP_PROGRESSION[100];
    const levelsBeyond100 = level - baseLevel;
    currentLevelXP = baseXP + (levelsBeyond100 * 2560);
    nextLevelXP = baseXP + ((levelsBeyond100 + 1) * 2560);
    expForCurrentLevel = normalizedExp - currentLevelXP;
    expNeededForNextLevel = 2560;
    displayTotalExp = expForCurrentLevel; // Show XP from current level start
  }
  
  return {
    level,
    currentExp: Math.max(0, expForCurrentLevel),
    expNeeded: expNeededForNextLevel,
    totalExp: displayTotalExp,
    progress: expNeededForNextLevel > 0 ? (Math.max(0, expForCurrentLevel) / expNeededForNextLevel) * 100 : 100,
    cumulativeExp: currentLevelXP,
    cumulativeExpNextLevel: nextLevelXP,
    isPhase2: level > 80
  };
};

// Experience calculation functions (keeping existing functionality)
export const calculateExperience = (ratings: Rating[] | null, achievements: Record<string, (AchievementExperienceItem | null)[]> | null | undefined,
  weeklyStreakData: unknown = null, challenges: ProgressExperienceItem[] = []) => {
  const experienceRatings = filterExperienceRatings(ratings || []);
  const keyedRatings = new Set<string>();
  let anonymousRatings = 0;
  experienceRatings.forEach((rating: Rating) => {
    const key = getRatingExperienceKey(rating);
    if (key) keyedRatings.add(key);
    else anonymousRatings += 1;
  });
  const filmExp = addSafeExperience(
    0,
    (keyedRatings.size + anonymousRatings) * 10
  );

  const achievementDetails: Record<string, { difficulty?: string; experience: number }> = {};
  const unlockedAchievements = Object.values(achievements || {})
    .flat()
    .filter((achievement): achievement is AchievementExperienceItem => Boolean(achievement?.unlocked));
  const achievementExp = sumUniqueExperience(
    unlockedAchievements,
    getAchievementExperienceKey,
    (achievement) => {
      const experience = getAchievementExperience(achievement);
      achievementDetails[String(achievement.title)] = {
        difficulty: achievement.difficulty,
        experience
      };
      return experience;
    }
  );

  let weeklyStreakExp = 0;
  if (isRecord(weeklyStreakData)) {
    weeklyStreakExp = calculateWeeklyStreakExperience(
      weeklyStreakData.count,
      weeklyStreakData
    );
  }

  const challengeExp = sumUniqueExperience(
    (isUnknownArray(challenges) ? challenges : [])
      .filter((challenge) => challenge?.status === "completed"),
    getProgressExperienceKey,
    (challenge) => {
      const explicitExperience = Number(challenge.experience);
      return Number.isFinite(explicitExperience) && explicitExperience > 0
        ? explicitExperience
        : getChallengeExperience(challenge.difficulty || "");
    }
  );

  return [filmExp, achievementExp, weeklyStreakExp, challengeExp]
    .reduce(addSafeExperience, 0);
};

export const calculateWeeklyStreakExperience = (streakCount: unknown, difficulty: unknown) => {
  if (isRecord(difficulty)) {
    const earnedStreakExperience = getWeeklyStreakLedgerExperience(difficulty);
    const quality = toNonNegativeNumber(difficulty.qualityBonusTotal);
    return addSafeExperience(earnedStreakExperience, quality);
  }
  return calculateSeriesExperience(streakCount || 0);
};

export const calculateSeriesExperience = (streakCount: unknown) => {
  const count = toNonNegativeNumber(streakCount);
  return addSafeExperience(0, count * 10 + (count * (count + 1)) / 2);
};

export const getStreakWeeklyIncrement = () => 1;

export const getQualityWeekExperience = (difficulty: unknown) => {
  switch (difficulty) {
    case 'iron': return 10;
    case 'bronze':
    case 'easy': return 30;
    case 'silver':
    case 'normal': return 50;
    case 'gold':
    case 'hard': return 80;
    case 'platinum': return 200;
    case 'diamond':
    case 'impossible': return 330;
    case 'legendary': return 450;
    default: return 0;
  }
};

export const getBaseStreakExperience = (difficulty: unknown) => {
  return getQualityWeekExperience(difficulty);
};

export const getMinFilmsForStreak = (difficulty: unknown) => {
  switch (difficulty) {
    case 'iron': return 1;
    case 'bronze':
    case 'easy': return 3;
    case 'silver':
    case 'normal': return 5;
    case 'gold':
    case 'hard': return 7;
    case 'platinum': return 14;
    case 'diamond':
    case 'impossible': return 21;
    case 'legendary': return 28;
    default: return 1;
  }
};

export const getStreakColor = (difficulty: string) => {
  switch (difficulty) {
    case 'iron': return '#9aa3ad';
    case 'bronze':
    case 'easy': return '#CD7F32';
    case 'silver':
    case 'normal': return '#C0C0C0';
    case 'gold':
    case 'hard': return '#FFD700';
    case 'platinum': return '#E5E4E2';
    case 'diamond':
    case 'impossible': return '#5ee7ff';
    case 'legendary': return '#b46cff';
    default: return '#CD7F32';
  }
};

export const canChangeDifficultyWithoutReset = (currentDifficulty: string, newDifficulty: string) => {
  const difficulties = ['iron', 'bronze', 'silver', 'gold', 'platinum', 'diamond', 'legendary'];
  const currentIndex = difficulties.indexOf(currentDifficulty);
  const newIndex = difficulties.indexOf(newDifficulty);

  return newIndex <= currentIndex;
};

// Level frame styling (keeping existing visual system)
export const getLevelFrame = (level: number) => {
  if (level >= 100) {
    return {
      name: 'Легенда кино',
      color: '#FFD700',
      gradient: 'linear-gradient(135deg, #FFD700, #FFA500, #FF6347)',
      frameType: 'legendary',
      shadow: '0 0 25px rgba(255, 215, 0, 0.9), 0 0 50px rgba(255, 69, 0, 0.5)',
      borderWidth: 8
    };
  } else if (level >= 90) {
    return {
      name: 'Платиновая звезда',
      color: '#E5E4E2',
      gradient: 'linear-gradient(135deg, #E5E4E2, #C0C0C0, #A8A8A8)',
      frameType: 'platinum',
      shadow: '0 0 20px rgba(229, 228, 226, 0.8)',
      borderWidth: 7
    };
  } else if (level >= 60) {
    return {
      name: 'Золотая пальмовая ветвь',
      color: '#DAA520',
      gradient: 'linear-gradient(135deg, #DAA520, #B8860B, #CD853F)',
      frameType: 'gold',
      shadow: '0 0 15px rgba(218, 165, 32, 0.7)',
      borderWidth: 6
    };
  } else if (level >= 30) {
    return {
      name: 'Серебряная кинолента',
      color: '#C0C0C0',
      gradient: 'linear-gradient(135deg, #C0C0C0, #A9A9A9, #808080)',
      frameType: 'silver',
      shadow: '0 0 12px rgba(192, 192, 192, 0.6)',
      borderWidth: 5
    };
  } else if (level >= 15) {
    return {
      name: 'Бронзовая звезда',
      color: '#CD7F32',
      gradient: 'linear-gradient(135deg, #CD7F32, #B87333, #A0522D)',
      frameType: 'bronze',
      shadow: '0 0 10px rgba(205, 127, 50, 0.5)',
      borderWidth: 4
    };
  } else if (level >= 5) {
    return {
      name: 'Начинающий киноман',
      color: '#4169E1',
      gradient: 'linear-gradient(135deg, #4169E1, #1E90FF, #00BFFF)',
      frameType: 'blue',
      shadow: '0 0 8px rgba(65, 105, 225, 0.4)',
      borderWidth: 4
    };
  } else {
    return {
      name: 'Новичок',
      color: '#808080',
      gradient: 'linear-gradient(135deg, #808080, #696969, #505050)',
      frameType: 'basic',
      shadow: '0 0 5px rgba(128, 128, 128, 0.3)',
      borderWidth: 3
    };
  }
};
